import { useReducer, useCallback } from 'react'

// Tipos para o estado dos filtros
export interface FiltrosState {
  tipo: string
  rankingTipo: string
  orderBy: 'nome' | 'valorEmPontos'
  orderDirection: 'asc' | 'desc'
  valorMinimo: string
  valorMaximo: string
  disponivel: string | null
  categoria: string | null
  posicaoMinima: string | null
  posicaoMaxima: string | null
  busca: string
}

// Ações para o reducer
export type FiltrosAction =
  | { type: 'SET_FILTRO'; payload: Partial<FiltrosState> }
  | { type: 'SET_BUSCA'; payload: string }
  | { type: 'SET_VALOR_MINIMO'; payload: string }
  | { type: 'SET_VALOR_MAXIMO'; payload: string }
  | { type: 'RESET_FILTROS' }
  | { type: 'UPDATE_FROM_URL'; payload: Partial<FiltrosState> }

// Estado inicial
const initialState: FiltrosState = {
  tipo: '',
  rankingTipo: 'geral',
  orderBy: 'nome',
  orderDirection: 'asc',
  valorMinimo: '',
  valorMaximo: '',
  disponivel: null,
  categoria: null,
  posicaoMinima: null,
  posicaoMaxima: null,
  busca: '',
}

// Função reducer
function filtrosReducer(
  state: FiltrosState,
  action: FiltrosAction,
): FiltrosState {
  switch (action.type) {
    case 'SET_FILTRO':
      return { ...state, ...action.payload }
    case 'SET_BUSCA':
      return { ...state, busca: action.payload }
    case 'SET_VALOR_MINIMO':
      return { ...state, valorMinimo: action.payload }
    case 'SET_VALOR_MAXIMO':
      return { ...state, valorMaximo: action.payload }
    case 'RESET_FILTROS':
      return initialState
    case 'UPDATE_FROM_URL':
      return { ...state, ...action.payload }
    default:
      return state
  }
}

// Hook personalizado para gerenciar filtros
export function useFiltrosReducer() {
  const [state, dispatch] = useReducer(filtrosReducer, initialState)

  // Funções auxiliares para facilitar o uso - memoizadas para evitar re-renders infinitos
  const setFiltro = useCallback(
    (payload: Partial<FiltrosState>) =>
      dispatch({ type: 'SET_FILTRO', payload }),
    [],
  )

  const setBusca = useCallback(
    (busca: string) => dispatch({ type: 'SET_BUSCA', payload: busca }),
    [],
  )

  const setValorMinimo = useCallback(
    (valor: string) => dispatch({ type: 'SET_VALOR_MINIMO', payload: valor }),
    [],
  )

  const setValorMaximo = useCallback(
    (valor: string) => dispatch({ type: 'SET_VALOR_MAXIMO', payload: valor }),
    [],
  )

  const resetFiltros = useCallback(
    () => dispatch({ type: 'RESET_FILTROS' }),
    [],
  )

  const updateFromUrl = useCallback(
    (payload: Partial<FiltrosState>) =>
      dispatch({ type: 'UPDATE_FROM_URL', payload }),
    [],
  )

  return {
    state,
    dispatch,
    setFiltro,
    setBusca,
    setValorMinimo,
    setValorMaximo,
    resetFiltros,
    updateFromUrl,
  }
}
